import React, {useEffect, useState} from "react";
import {Button, Modal} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faExclamationCircle, faInfoCircle, faQuestionCircle} from "@fortawesome/free-solid-svg-icons";
import ModalControls from "../common/ModalControls";
import {useLocation, useNavigate} from "react-router-dom";
import {defaultFormState} from "../../../defaults/Forms";
import {businessPartnerDefault} from "../../../defaults/Items";
import {
  compareData,
  onCloseDetails,
  onItemsListDeleteButtonClick,
  onSaveAndClose,
  restoreFormData
} from "../../../helpers/ComponentHelper";
import AppComponentCardHeader from "../common/AppComponentCardHeader";
import LoadingDataDiv from "../common/LoadingDataDiv";
import DeleteWarningModal from "../common/DeleteWarningModal";
import TextInput from "../../elements/TextInput";
import TextArea from "../../elements/TextArea";
import useCrud from "../../../hooks/useCrud";
import {Table} from "../../table/Table";
import Select from "../../elements/Select";
import BusinessPartnerAddressCard from "./BusinessPartnerAddressCard";
import BusinessPartnerContractsCard from "./BusinessPartnerContractsCard";
import Toggler from "../../elements/Toggler";
import NumberInput from "../../elements/NumberInput";
import {ToastContainer} from "react-toastify";
import {toastError, toastSuccess} from "../../../helpers/Toast";
import "../../../css/DictionaryForms.css";
import "../../../css/ResourceForms.css";
import "../../../css/ResourceTables.css";

const BusinessPartner = () => {
  const dataUrl = "/business-partners";
  const legalEntityUrl = "/legal-entities";
  const countryUrl = "/countries";
  const defaultItem = businessPartnerDefault;
  const itemName = "kontrahent";
  const navigate = useNavigate();
  const location = useLocation();
  const {createItem, updateItem, deleteItem, getItems} = useCrud(dataUrl);

  const [loading, setLoading] = useState(true);
  const [allowDelete, setAllowDelete] = useState(null);
  const [currentFormState, setCurrentFormState] = useState(defaultFormState);
  const [itemsList, setItems] = useState([]);
  const [legalEntitiesList, setLegalEntities] = useState([]);
  const [countriesList, setCountries] = useState([]);
  const [currentItem, setCurrentItem] = useState(defaultItem);
  const [backupItem, setBackupItem] = useState(defaultItem);
  const [itemChanged, setItemChanged] = useState(false);
  const [updateConflict, setUpdateConflict] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [contractsList] = useState([]);

  const columns = [
    {label: "Id", accessor: "id", sortable: true, searchable: false, visible: false},
    {label: "Nazwa", accessor: "name", sortable: true, searchable: true},
    {label: "Skrócona nazwa", accessor: "shortName", sortable: true, searchable: true},
    {label: "Uwagi", accessor: "notes", sortable: true, searchable: true},
    {label: "Aktywny", accessor: "active", sortable: true, searchable: true, type: "bool"},
    {label: "Zatrudniający", accessor: "hiringCompany", sortable: true, searchable: true, type: "bool"},
    {label: "Edycja", accessor: "editBtn", sortable: false, searchable: false},
    {label: "Usuń", accessor: "deleteBtn", sortable: false, searchable: false}
  ];

  const primaryCountry = {
    label: "Kraj",
    itemName: "billingAddressCountry",
    propertyName: "billingAddressCountryId"
  };

  const secondaryCountry = {
    label: "Kraj",
    itemName: "secondaryAddressCountry",
    propertyName: "secondaryAddressCountryId"
  };

  const primaryAddress = {
    street: "billingAddressStreet",
    streetNumber: "billingAddressStreetNumber",
    postalCode: "billingAddressPostalCode",
    city: "billingAddressCity"
  };

  const secondaryAddress = {
    street: "secondaryAddressStreet",
    streetNumber: "secondaryAddressStreetNumber",
    postalCode: "secondaryAddressPostalCode",
    city: "secondaryAddressCity"
  };

  const state = {
    itemsList,
    setItems,
    currentItem,
    setCurrentItem,
    currentFormState,
    setCurrentFormState,
    defaultItem,
    backupItem,
    setBackupItem,
    itemChanged,
    setItemChanged,
    setAllowDelete,
    setRelatedItems: null
  };

  const onDelete = async () => {
    const response = await deleteItem(`${dataUrl}/${currentItem.id}`, currentItem.id, state);
    if (response === 401 || response === 403) {
      navigate("/login", {state: {from: location}, replace: true});
    }
  };

  const showSaveError = message => {
    const text = message || "Nie udało się zapisać kontrahenta.";
    setSaveError(text);
    toastError(text);
  };

  const onSaveItemClick = async event => {
    event.preventDefault();
    setUpdateConflict(false);
    setSaveError("");

    const name = typeof currentItem.name === "string" ? currentItem.name.trim() : "";
    const legalEntityId = Number(currentItem.legalEntity?.id || 0);

    if (!name) {
      const input = document.getElementById("name");
      input?.classList.add("form-input-invalid");
      if (input) input.placeholder = "ten element jest wymagany";
      showSaveError("Podaj nazwę kontrahenta.");
      return;
    }

    if (!legalEntityId) {
      document.getElementById("legalEntity")?.classList.add("form-input-invalid");
      showSaveError("Wybierz formę prawną kontrahenta.");
      return;
    }

    const relation = value => Number(value?.id || 0) > 0 ? {id: Number(value.id)} : null;
    const item = {
      id: Number(currentItem.id || 0) > 0 ? Number(currentItem.id) : null,
      name,
      shortName: typeof currentItem.shortName === "string" ? currentItem.shortName.trim() || null : null,
      legalEntity: {id: legalEntityId},
      notes: typeof currentItem.notes === "string" ? currentItem.notes.trim() || null : null,
      nip: typeof currentItem.nip === "string" ? currentItem.nip.trim() || null : null,
      primaryPhone: typeof currentItem.primaryPhone === "string" ? currentItem.primaryPhone.trim() || null : null,
      primaryEmail: typeof currentItem.primaryEmail === "string" ? currentItem.primaryEmail.trim() || null : null,
      billingAddressStreet: typeof currentItem.billingAddressStreet === "string" ? currentItem.billingAddressStreet.trim() || null : null,
      billingAddressStreetNumber: typeof currentItem.billingAddressStreetNumber === "string" ? currentItem.billingAddressStreetNumber.trim() || null : null,
      billingAddressPostalCode: typeof currentItem.billingAddressPostalCode === "string" ? currentItem.billingAddressPostalCode.trim() || null : null,
      billingAddressCity: typeof currentItem.billingAddressCity === "string" ? currentItem.billingAddressCity.trim() || null : null,
      billingAddressCountry: relation(currentItem.billingAddressCountry),
      secondaryAddressStreet: typeof currentItem.secondaryAddressStreet === "string" ? currentItem.secondaryAddressStreet.trim() || null : null,
      secondaryAddressStreetNumber: typeof currentItem.secondaryAddressStreetNumber === "string" ? currentItem.secondaryAddressStreetNumber.trim() || null : null,
      secondaryAddressPostalCode: typeof currentItem.secondaryAddressPostalCode === "string" ? currentItem.secondaryAddressPostalCode.trim() || null : null,
      secondaryAddressCity: typeof currentItem.secondaryAddressCity === "string" ? currentItem.secondaryAddressCity.trim() || null : null,
      secondaryAddressCountry: relation(currentItem.secondaryAddressCountry),
      hiringCompany: !!currentItem.hiringCompany,
      active: currentItem.active !== false,
      commission: Number.isFinite(Number(currentItem.commission)) ? Number(currentItem.commission) : 0
    };

    try {
      const response = currentFormState.formAddingDataMode
        ? await createItem(dataUrl, item, state)
        : await updateItem(`${dataUrl}/${item.id}`, item, state);

      const status = Number(response?.status || 0);
      if (status === 401 || status === 403) {
        navigate("/login", {state: {from: location}, replace: true});
        return;
      }
      if (status === 409) {
        setUpdateConflict(true);
        showSaveError(typeof response?.data === "string" ? response.data : "Istnieje już kontrahent o takich danych.");
        return;
      }
      if (status < 200 || status >= 300) {
        showSaveError(typeof response?.data === "string" ? response.data : response?.message || "Nie udało się zapisać kontrahenta.");
        return;
      }

      toastSuccess(currentFormState.formAddingDataMode ? "Kontrahent został dodany." : "Dane kontrahenta zostały zapisane.");
      onSaveAndClose({state});
    } catch (error) {
      showSaveError(error?.response?.data || error?.message || "Nie udało się zapisać kontrahenta.");
    }
  };

  const onClose = () => {
    setUpdateConflict(false);
    setSaveError("");
    onCloseDetails({state});
  };

  useEffect(() => {
    if (updateConflict) setUpdateConflict(false);
    if (saveError) setSaveError("");
    compareData(currentFormState, setCurrentFormState, currentItem, backupItem);
  }, [itemChanged]);

  useEffect(() => {
    if (allowDelete !== null) {
      onItemsListDeleteButtonClick(currentFormState, setCurrentFormState, itemName, allowDelete, currentItem.name);
    }
  }, [allowDelete]);

  useEffect(() => {
    const getData = async () => {
      const [itemsData, legalEntityData, countriesData] = await Promise.all([
        getItems(dataUrl),
        getItems(legalEntityUrl),
        getItems(countryUrl)
      ]);

      if (itemsData.status === 200 && legalEntityData.status === 200 && countriesData.status === 200) {
        setItems(itemsData.data);
        setLegalEntities(legalEntityData.data);
        setCountries(countriesData.data);
        setLoading(false);
      } else if ([itemsData.status, legalEntityData.status, countriesData.status].some(status => status === 401 || status === 403)) {
        navigate("/login", {state: {from: location}, replace: true});
      } else {
        alert("Nie udało się pobrać danych.");
      }
    };
    getData();
  }, []);

  const addDataButtonProps = {
    setCurrentItem,
    setBackupItem,
    defaultItem,
    currentFormState,
    setCurrentFormState,
    formDescription: "",
    formHeader: "Nowy kontrahent",
    buttonTitle: "Dodaj kontrahenta"
  };

  let dataSectionContent;
  if (loading) {
    dataSectionContent = <LoadingDataDiv/>;
  } else if (itemsList.length > 0) {
    dataSectionContent = (
      <Table
        rows={itemsList}
        columns={columns}
        state={state}
        tableClassName="resource-object-table business-partner-table"
        tableContainerClassName="resource-object-table-scroll"
        checkRelatedItems={() => setAllowDelete(true)}
        formHeader={`Edycja ${itemName}`}
        relatedItemsUrl={null}
        exportFileName="kontrahenci.xlsx"
      />
    );
  } else {
    dataSectionContent = (
      <div className="resource-empty-state">
        <FontAwesomeIcon icon={faExclamationCircle}/>
        <span>Nie znaleziono żadnych kontrahentów. Dodaj pierwszy wpis.</span>
      </div>
    );
  }

  const modalTitle = currentFormState.formAddingDataMode
    ? "Nowy kontrahent"
    : `Edycja kontrahenta: ${currentItem.name || "bez nazwy"}`;

  return (
    <div id="layoutSidenav_content" className="dictionary-page resource-page">
      <ToastContainer/>
      <AppComponentCardHeader title="Kontrahenci" buttonProps={addDataButtonProps}/>

      <div className="container-fluid px-4 py-3">
        <div className="card shadow-sm mb-5 resource-list-card resource-object-list-card business-partner-list-card">
          {dataSectionContent}
        </div>
      </div>

      <Modal
        show={currentFormState.showForm}
        onHide={onClose}
        backdrop="static"
        keyboard={false}
        scrollable
        className="dictionary-form-modal"
        dialogClassName="dictionary-form-modal-dialog resource-form-modal-dialog resource-form-modal-dialog-wide"
      >
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="dictionary-form-intro">
            <div>
              <strong>{currentFormState.formAddingDataMode ? "Dodajesz nowego kontrahenta" : "Edytujesz dane kontrahenta"}</strong>
              <span>Dane podstawowe, kontaktowe, adresowe i informacje wykorzystywane przy zatrudnieniu.</span>
            </div>
            {!currentFormState.formAddingDataMode && (
              <Button size="sm" variant="outline-secondary" onClick={() => {
                setUpdateConflict(false);
                restoreFormData({state});
              }}>
                Przywróć wartości
              </Button>
            )}
          </div>

          <form id="add-business-partner-form" name="add-business-partner-form" className="resource-form-grid">
            <section className="resource-form-section resource-form-section-full">
              <div className="resource-form-section-title">
                <div>
                  <strong>Dane podstawowe</strong>
                  <span>Identyfikacja kontrahenta i forma prawna.</span>
                </div>
              </div>
              <div className="resource-form-section-grid">
                <div className="dictionary-form-field">
                  <label htmlFor="name"><span className="required">*</span> Nazwa</label>
                  <TextInput propertyName="name" required={true} state={state}/>
                </div>
                <div className="dictionary-form-field">
                  <label htmlFor="shortName">Skrócona nazwa</label>
                  <TextInput propertyName="shortName" maxlen="100" required={false} state={state}/>
                </div>
                <div className="dictionary-form-field">
                  <label htmlFor="nip">NIP</label>
                  <TextInput propertyName="nip" maxlen="20" required={false} state={state}/>
                </div>
                <div className="dictionary-form-field">
                  <label htmlFor="legalEntity"><span className="required">*</span> Forma prawna</label>
                  <Select
                    label="Forma prawna"
                    propertyName="legalEntityId"
                    required={true}
                    state={state}
                    itemsList={legalEntitiesList}
                    itemName="legalEntity"
                    displayNames={["name"]}
                  />
                </div>
                <div className="dictionary-form-field dictionary-form-field-full">
                  <label htmlFor="notes">Uwagi</label>
                  <TextArea propertyName="notes" required={false} rows="3" state={state}/>
                </div>
              </div>
            </section>

            <section className="resource-form-section">
              <div className="resource-form-section-title">
                <div>
                  <strong>Kontakt</strong>
                  <span>Podstawowe dane kontaktowe kontrahenta.</span>
                </div>
              </div>
              <div className="resource-form-section-grid">
                <div className="dictionary-form-field">
                  <label htmlFor="primaryPhone">Główny telefon</label>
                  <TextInput propertyName="primaryPhone" state={state}/>
                </div>
                <div className="dictionary-form-field">
                  <label htmlFor="primaryEmail">Główny adres e-mail</label>
                  <TextInput propertyName="primaryEmail" state={state}/>
                </div>
              </div>
            </section>

            <section className="resource-form-section">
              <div className="resource-form-section-title">
                <div>
                  <strong>Status i zatrudnienie</strong>
                  <span>Ustawienia wykorzystywane w kartotekach pracowników i kalkulacji kosztów.</span>
                </div>
              </div>
              <div className="resource-hiring-row">
                <div className="resource-toggle-row">
                  <div>
                    <strong>Firma zatrudniająca</strong>
                    <small>Kontrahent może być wskazany jako podmiot zatrudniający.</small>
                  </div>
                  <Toggler propertyName="hiringCompany" state={state}/>
                </div>
                <div className="dictionary-form-field resource-commission-field">
                  <label htmlFor="commission">Prowizja (%)</label>
                  <NumberInput propertyName="commission" defaultValue="0" min="0.0" max="99999" required="true" state={state} disabled={!state.currentItem.hiringCompany}/>
                </div>
                <div className="resource-toggle-row">
                  <div>
                    <strong>Aktywny</strong>
                    <small>Nieaktywni kontrahenci pozostają w danych historycznych.</small>
                  </div>
                  <Toggler propertyName="active" state={state}/>
                </div>
              </div>
              <div className="form_tooltip mt-2">
                <FontAwesomeIcon icon={faQuestionCircle}/>
                <span className="form_tooltip_text">Prowizja jest doliczana do kosztu stawki godzinowej pracownika zatrudnionego przez ten podmiot.</span>
              </div>
            </section>

            <section className="resource-form-section resource-form-section-full">
              <div className="resource-form-section-title">
                <div>
                  <strong>Adresy</strong>
                  <span>Adres podatkowy oraz adres korespondencyjny.</span>
                </div>
              </div>
              <div className="resource-form-section-grid">
                <div className="resource-subcard">
                  <BusinessPartnerAddressCard
                    cardHeader="Adres podatkowy"
                    state={state}
                    addressProps={primaryAddress}
                    countriesList={countriesList}
                    countryProps={primaryCountry}
                  />
                </div>
                <div className="resource-subcard">
                  <BusinessPartnerAddressCard
                    cardHeader="Adres korespondencyjny"
                    state={state}
                    addressProps={secondaryAddress}
                    countriesList={countriesList}
                    countryProps={secondaryCountry}
                  />
                </div>
              </div>
            </section>

            {!currentFormState.formAddingDataMode && (
              <section className="resource-form-section resource-form-section-full">
                <div className="resource-form-section-title">
                  <div>
                    <strong>Kontrakty</strong>
                    <span>Powiązane umowy i historia współpracy.</span>
                  </div>
                </div>
                <div className="resource-subcard"><BusinessPartnerContractsCard cardHeader="Kontrakty" state={state} contractsList={contractsList}/></div>
              </section>
            )}
          </form>

          {(updateConflict || saveError) && (
            <div className="dictionary-form-error">
              <FontAwesomeIcon icon={faInfoCircle}/>
              <span>{saveError || "Nie można zapisać kontrahenta, ponieważ istnieje już wpis o tej nazwie, skróconej nazwie lub NIP-ie."}</span>
            </div>
          )}
        </Modal.Body>
        <ModalControls onDelete={onDelete} onCloseDetails={onClose} onSubmit={onSaveItemClick} state={state}/>
      </Modal>

      <DeleteWarningModal state={state} onDelete={onDelete} deleteItemName={itemName}/>
    </div>
  );
};

export default BusinessPartner;
